//Users Component: Displays list of users, allows adding/editing/deleting users and managing their access permissions.
import {
  Box,
  Button,
  IconButton,
  Modal,
  Popover,
  Stack,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AddIcon,
  VisibilityOutlinedIcon,
  MoreVertIcon,
  DeleteOutlineIcon,
  EditIcon,
} from "../../utilities/icons";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import EmployeeModal from "./UserModal";
import { DataGrid } from "@mui/x-data-grid";
import AddAccess from "./AddAccess";
import Cookies from "js-cookie";
import {
  DeleteUser,
  createUser,
  getUsers,
  updateUser,
} from "../../apiCalls/Apicalls";
import { DEFAULT_DEPARTMENTS } from "../../constants/departments";
import ViewUser from "./ViewUser";
import { theme } from "../../utilities/dataGridHelpers";

function ListOfUsers() {
  const emptyUserData = {
    FirstName: "",
    LastName: "",
    EmpId: "",
    Contact: "",
    Email: "",
    JobTitle: "",
    EmployeeType: "",
    SpaceName: "",
    Access: [],
    hierarchyLevel: "Employee",
    departmentId: "",
    team: "",
    reportingToId: "",
    reportingToName: "",
    managerId: "",
    managerName: "",
    teamLeadId: "",
    teamLeadName: "",
    hrId: "",
    hrName: "",
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [selectedareas, setSelectedareas] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [rows, setRows] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [modalOpen, setModelOpen] = useState(false);
  const [initialUserData, setInitialUserData] = useState({});
  const [filterMode, setFilterMode] = useState("all");
  const [search, setSearch] = useState("");
  const [userData, setUserData] = useState(emptyUserData);
  const [deleteModalopen, setDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [submittingUser, setSubmittingUser] = useState(false);
  const [deletingUserLoading, setDeletingUserLoading] = useState(false);

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const buildDepartmentId = useCallback(
    (department = "") =>
      department
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, "-")
        .replace(/^-+|-+$/g, ""),
    [],
  );

  const getUserDisplayName = (user) =>
    user?.FullName ||
    user?.fullName ||
    [user?.FirstName, user?.LastName].filter(Boolean).join(" ") ||
    user?.Email ||
    "";

  const normalizeUserForForm = (user = {}) => ({
    ...emptyUserData,
    ...user,
    hierarchyLevel: user.hierarchyLevel || user.HierarchyLevel || "Employee",
    departmentId: user.departmentId || buildDepartmentId(user.SpaceName || ""),
    team: user.team || user.SpaceName || "",
    reportingToId: user.reportingToId || "",
    reportingToName: user.reportingToName || "",
    managerId: user.managerId || "",
    managerName: user.managerName || "",
    teamLeadId: user.teamLeadId || "",
    teamLeadName: user.teamLeadName || "",
    hrId: user.hrId || "",
    hrName: user.hrName || "",
  });

  const hierarchyUsers = useMemo(() => rows, [rows]);

  const getHierarchyOptions = (department, currentEmpId) => {
    const sameDepartment = hierarchyUsers.filter(
      (user) =>
        user.EmpId !== currentEmpId &&
        (!department || user.SpaceName === department),
    );
    const allExceptCurrent = hierarchyUsers.filter(
      (user) => user.EmpId !== currentEmpId,
    );
    const byLevel = (level) =>
      sameDepartment.filter((user) => user.hierarchyLevel === level);

    return {
      admins: allExceptCurrent.filter(
        (user) => user.Role === "Admin" || user.hierarchyLevel === "Admin",
      ),
      hrs: byLevel("HR").length
        ? byLevel("HR")
        : allExceptCurrent.filter((user) => user.hierarchyLevel === "HR"),
      managers: byLevel("Manager").length
        ? byLevel("Manager")
        : allExceptCurrent.filter((user) => user.hierarchyLevel === "Manager"),
      teamLeads: byLevel("Team Lead").length
        ? byLevel("Team Lead")
        : allExceptCurrent.filter(
            (user) => user.hierarchyLevel === "Team Lead",
          ),
    };
  };

  const pickUserRef = (users, selectedId) => {
    const selected =
      users.find((user) => user.EmpId === selectedId) || users[0] || null;
    return selected
      ? { id: selected.EmpId, name: getUserDisplayName(selected) }
      : { id: "", name: "" };
  };

  const applyHierarchyDefaults = (incomingUserData) => {
    const next = {
      ...incomingUserData,
      departmentId:
        incomingUserData.departmentId ||
        buildDepartmentId(incomingUserData.SpaceName || ""),
      team: incomingUserData.team || incomingUserData.SpaceName || "",
    };
    const options = getHierarchyOptions(next.SpaceName, next.EmpId);
    const level = next.hierarchyLevel || "Employee";
    const admin = pickUserRef(options.admins, next.reportingToId);
    const hr = pickUserRef(options.hrs, next.hrId);
    const manager = pickUserRef(options.managers, next.managerId);
    const teamLead = pickUserRef(options.teamLeads, next.teamLeadId);

    if (level === "Admin") {
      return {
        ...next,
        reportingToId: "",
        reportingToName: "",
        hrId: "",
        hrName: "",
        managerId: "",
        managerName: "",
        teamLeadId: "",
        teamLeadName: "",
      };
    }

    if (level === "HR") {
      return {
        ...next,
        reportingToId: admin.id,
        reportingToName: admin.name,
        hrId: "",
        hrName: "",
        managerId: "",
        managerName: "",
        teamLeadId: "",
        teamLeadName: "",
      };
    }

    if (level === "Manager") {
      return {
        ...next,
        reportingToId: hr.id,
        reportingToName: hr.name,
        hrId: hr.id,
        hrName: hr.name,
        managerId: "",
        managerName: "",
        teamLeadId: "",
        teamLeadName: "",
      };
    }

    if (level === "Team Lead") {
      return {
        ...next,
        reportingToId: manager.id,
        reportingToName: manager.name,
        hrId: manager.id
          ? hierarchyUsers.find((user) => user.EmpId === manager.id)?.hrId ||
            hr.id
          : hr.id,
        hrName: manager.id
          ? hierarchyUsers.find((user) => user.EmpId === manager.id)?.hrName ||
            hr.name
          : hr.name,
        managerId: manager.id,
        managerName: manager.name,
        teamLeadId: "",
        teamLeadName: "",
      };
    }

    return {
      ...next,
      reportingToId: teamLead.id,
      reportingToName: teamLead.name,
      teamLeadId: teamLead.id,
      teamLeadName: teamLead.name,
      managerId: teamLead.id
        ? hierarchyUsers.find((user) => user.EmpId === teamLead.id)
            ?.managerId || manager.id
        : manager.id,
      managerName: teamLead.id
        ? hierarchyUsers.find((user) => user.EmpId === teamLead.id)
            ?.managerName || manager.name
        : manager.name,
      hrId: teamLead.id
        ? hierarchyUsers.find((user) => user.EmpId === teamLead.id)?.hrId ||
          hr.id
        : hr.id,
      hrName: teamLead.id
        ? hierarchyUsers.find((user) => user.EmpId === teamLead.id)?.hrName ||
          hr.name
        : hr.name,
    };
  };

  const handleModalOpen = (user = null) => {
    const isUserRecord =
      user &&
      typeof user === "object" &&
      !("target" in user) &&
      !("currentTarget" in user);

    if (isUserRecord) {
      const normalized = normalizeUserForForm(user);
      setUserData(normalized);
      setInitialUserData(normalized);
    } else {
      setUserData(emptyUserData);
      setInitialUserData(emptyUserData);
    }
    setFormErrors({});
    setIsSubmit(false);
    setModelOpen(true);
  };
  const handleModalClose = () => {
    setUserData(emptyUserData);
    setInitialUserData(emptyUserData);
    setModelOpen(false);
    setFormErrors({});
    setIsSubmit(false);
  };

  const fetchUsers = useCallback(() => {
    setLoading(true);
    axios
      .get(getUsers)
      .then((response) => {
        const dataWithId = response.data.map((item) => ({
          ...item,
          hierarchyLevel:
            item.hierarchyLevel || item.HierarchyLevel || "Employee",
          departmentId:
            item.departmentId || buildDepartmentId(item.SpaceName || ""),
          team: item.team || item.SpaceName || "",
          id: item?.EmpId,
        }));
        setRows(dataWithId);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching user data:", error);
        setLoading(false);
      });
  }, [buildDepartmentId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setUserData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleHierarchyChange = (event) => {
    const { name, value } = event.target;
    setUserData((previous) =>
      applyHierarchyDefaults({
        ...previous,
        [name]: value,
        ...(name === "SpaceName"
          ? { departmentId: buildDepartmentId(value), team: value }
          : {}),
      }),
    );
  };

  const handleReportingUserChange = (fieldPrefix, users) => (event) => {
    const selected = pickUserRef(users, event.target.value);
    setUserData((previous) => {
      const next = {
        ...previous,
        [`${fieldPrefix}Id`]: selected.id,
        [`${fieldPrefix}Name`]: selected.name,
      };

      if (fieldPrefix === "teamLead") {
        const lead = hierarchyUsers.find((user) => user.EmpId === selected.id);
        next.reportingToId = selected.id;
        next.reportingToName = selected.name;
        next.managerId = lead?.managerId || next.managerId;
        next.managerName = lead?.managerName || next.managerName;
        next.hrId = lead?.hrId || next.hrId;
        next.hrName = lead?.hrName || next.hrName;
      }

      if (fieldPrefix === "manager") {
        const manager = hierarchyUsers.find(
          (user) => user.EmpId === selected.id,
        );
        next.reportingToId = selected.id;
        next.reportingToName = selected.name;
        next.hrId = manager?.hrId || next.hrId;
        next.hrName = manager?.hrName || next.hrName;
      }

      if (fieldPrefix === "hr") {
        next.reportingToId = selected.id;
        next.reportingToName = selected.name;
      }

      return next;
    });
  };

  const validate = (values) => {
    const errors = {};
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const specialCharsRegex = /[!@#$%^&*(),?":{}|<>]/;
    const specialCharsRegexWithoutDot = /[!@#$%^&*(),.?":{}|<>]/;
    const emojiRegex =
      /[\u{1F600}-\u{1F6FF}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}]/u;
    const leadingTrailingSpacesRegex = /^\s+|\s+$/g;

    if (!values.Email) {
      errors.Email = "Field is required";
    } else if (
      !emailRegex.test(values.Email) ||
      values.Email.startsWith(".") ||
      values.Email.includes("..") ||
      values.Email.startsWith("@") ||
      values.Email.endsWith(".") ||
      values.Email.includes(" ") ||
      values.Email.lastIndexOf("@") !== values.Email.indexOf("@")
    ) {
      errors.Email = "Invalid email format";
    }

    if (!values.Contact) {
      errors.Contact = "* Field is required";
    } else if (!/^\d{10}$/.test(values.Contact)) {
      errors.Contact = "Contact number should contain 10 digits";
    }

    const textFields = [
      "FirstName",
      "LastName",
      "EmpId",
      "Contact",
      "EmployeeType",
      "JobTitle",
      "SpaceName",
    ];

    textFields.forEach((field) => {
      const value = values[field];
      if (!value) {
        errors[field] = "* Field is required";
      } else if (
        (field !== "JobTitle" && specialCharsRegexWithoutDot.test(value)) ||
        (field === "JobTitle" && specialCharsRegex.test(value)) ||
        emojiRegex.test(value) ||
        leadingTrailingSpacesRegex.test(value)
      ) {
        errors[field] =
          "Field should not contain special characters, emojis, or leading/trailing spaces";
      }
    });

    return errors;
  };

  useEffect(() => {
    if (isSubmit) {
      setFormErrors(validate(userData));
    }
  }, [userData, isSubmit]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submittingUser) {
      return;
    }
    setIsSubmit(true);
    const errors = validate(userData);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      setSubmittingUser(true);
      const config = {
        headers: {
          Authorization: Cookies.get("jwtToken"),
          "Content-Type": "application/json",
        },
      };

      const isEditMode = Boolean(initialUserData?._id);
      const payload = applyHierarchyDefaults(userData);

      if (isEditMode) {
        const response = await axios.put(
          `${updateUser}/${userData.EmpId}`,
          payload,
          config,
        );
        if (response.status === 200) {
          toast.success("User details updated successfully");
        }
      } else {
        await axios.post(createUser, payload, config);
        toast.success("User created successfully");
      }

      fetchUsers();
      handleModalClose();
    } catch (error) {
      if (error.response?.data?.error === "User already exists.") {
        toast.error("User already exists");
      } else {
        toast.error("Unable to save user details");
      }
    } finally {
      setSubmittingUser(false);
    }
  };

  const handleDelete = async () => {
    if (deletingUserLoading) {
      return;
    }
    try {
      setDeletingUserLoading(true);
      const empid = deleteUser?._id;
      if (!empid) {
        return;
      }
      const config = {
        headers: {
          Authorization: Cookies.get("jwtToken"),
          "Content-Type": "application/json",
        },
      };
      await axios.delete(`${DeleteUser}/${empid}`, config);
      toast.success("User deleted successfully");
      fetchUsers();
      setDeleteModalOpen(false);
    } catch (error) {
      toast.error("Error deleting user");
    } finally {
      setDeletingUserLoading(false);
    }
  };

  const handleOpen = (row) => {
    setOpen(true);
    setCurrentRow(row);
  };
  const handleClose = () => setOpen(false);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const hasAccess = row.Access && row.Access.length > 0;
      const matchesFilter =
        filterMode === "all" ||
        (filterMode === "access" && hasAccess) ||
        (filterMode === "no-access" && !hasAccess);

      const haystack =
        `${row.EmpId} ${row.FullName} ${row.Email} ${row.SpaceName} ${row.JobTitle}`.toLowerCase();
      const matchesSearch = haystack.includes(search.toLowerCase());
      return matchesFilter && matchesSearch;
    });
  }, [rows, filterMode, search]);

  const departmentOptions = useMemo(() => {
    const rowDepartments = rows
      .map((row) => row?.SpaceName?.trim())
      .filter(Boolean);

    return [...new Set([...DEFAULT_DEPARTMENTS, ...rowDepartments])].sort(
      (first, second) => first.localeCompare(second),
    );
  }, [rows]);

  const columns = [
    {
      field: "EmpId",
      headerName: "Emp ID",
      sortable: false,
      flex: 0.8,
      // minWidth: 120,
    },
    {
      field: "FullName",
      headerName: "Full Name",
      sortable: false,
      flex: 1.2,
      // minWidth: 180,
    },
    {
      field: "Email",
      headerName: "Email",
      sortable: false,
      flex: 1.4,
      // minWidth: 220,
    },
    {
      field: "SpaceName",
      headerName: "Department",
      sortable: false,
      flex: 1,
      // minWidth: 160,
    },
    {
      field: "hierarchyLevel",
      headerName: "Hierarchy Role",
      sortable: false,
      flex: 0.9,
      // minWidth: 150,
      renderCell: (params) => (
        <span className="">{params.row.hierarchyLevel || "Employee"}</span>
      ),
    },
    {
      field: "reportingToName",
      headerName: "Reports To",
      sortable: false,
      flex: 1,
      // minWidth: 170,
      renderCell: (params) => params.row.reportingToName || "-",
    },
    {
      field: "AccessNote",
      headerName: "Access",
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        const hasAccess = params.row.Access && params.row.Access.length > 0;
        return (
          <span className={`ax-chip ${hasAccess ? "green" : "red"}`}>
            {hasAccess ? "Access Granted" : "No Access"}
          </span>
        );
      },
    },
    {
      field: "Actions",
      headerName: "Actions",
      sortable: false,
      flex: 0.8,
      renderCell: (params) => (
        <Box>
          <Stack direction={"row"} gap={0.5}>
            <Tooltip title="View Details">
              <IconButton
                aria-label="view"
                size="small"
                onClick={() => handleOpen(params.row)}
                sx={{
                  color: "var(--ax-ink-2)",
                  "&:hover": {
                    color: "var(--ax-blue)",
                    bgcolor: "var(--ax-blue-overlay)",
                  },
                }}
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="More">
              <IconButton
                onClick={(event) => {
                  setCurrentRow(params.row);
                  handlePopoverOpen(event);
                }}
                size="small"
                sx={{
                  color: "var(--ax-ink-2)",
                  "&:hover": {
                    color: "var(--ax-blue)",
                    bgcolor: "var(--ax-blue-overlay)",
                  },
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      ),
    },
  ];

  return (
    <>
      <div className="ax-page-grid">
        <div className="ax-card ax-table-shell" style={{ padding: 0 }}>
          <div className="ax-grid-toolbar">
            <div className="ax-filters">
              <button
                className={`ax-filter-chip${filterMode === "all" ? " active" : ""}`}
                onClick={() => setFilterMode("all")}
                type="button"
              >
                All Users
              </button>
              <button
                className={`ax-filter-chip${filterMode === "access" ? " active" : ""}`}
                onClick={() => setFilterMode("access")}
                type="button"
              >
                Access Granted
              </button>
              <button
                className={`ax-filter-chip${
                  filterMode === "no-access" ? " active" : ""
                }`}
                onClick={() => setFilterMode("no-access")}
                type="button"
              >
                No Access
              </button>
            </div>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.25}
              sx={{ minWidth: 0 }}
            >
              <TextField
                placeholder="Search users..."
                size="small"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={{ minWidth: { xs: "100%", sm: 240 } }}
              />

              <Button
                variant="contained"
                onClick={() => handleModalOpen()}
                startIcon={<AddIcon fontSize="small" />}
                size="small"
                sx={{ whiteSpace: "nowrap", px: 2 }}
              >
                Add User
              </Button>
            </Stack>
          </div>
          <Box className="ax-table-scroll">
            <ThemeProvider theme={theme}>
              <DataGrid
                sx={{
                  width: "100%",
                  height: "calc(100vh - 280px)",
                  minHeight: 420,
                }}
                rowHeight={62}
                loading={loading}
                disableColumnMenu
                getRowId={(row) => row._id}
                rows={filteredRows}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 10 },
                  },
                }}
                pageSizeOptions={[10, 25, 50]}
                disableRowSelectionOnClick
              />
            </ThemeProvider>
          </Box>
        </div>
      </div>

      <EmployeeModal
        modalOpen={modalOpen}
        handleModalClose={handleModalClose}
        handleModalOpen={handleModalOpen}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        userData={userData}
        formErrors={formErrors}
        setIsSubmit={setIsSubmit}
        initialUserData={initialUserData}
        departmentOptions={departmentOptions}
        hierarchyOptions={getHierarchyOptions(
          userData.SpaceName,
          userData.EmpId,
        )}
        handleHierarchyChange={handleHierarchyChange}
        handleReportingUserChange={handleReportingUserChange}
        buildDepartmentId={buildDepartmentId}
        getUserDisplayName={getUserDisplayName}
        submitting={submittingUser}
      />
      <AddAccess
        open={openDrawer}
        onClose={() => {
          setOpenDrawer(false);
          setCurrentRow(null);
          setSelectedareas([]);
          setUserData({});
        }}
        selectedRow={currentRow}
        filteredOptions={filteredOptions}
        setFilteredOptions={setFilteredOptions}
        userData={userData}
        selectedareas={selectedareas}
        setSelectedareas={setSelectedareas}
        fetchUsers={fetchUsers}
      />
      <Modal
        open={deleteModalopen}
        onClose={
          deletingUserLoading ? undefined : () => setDeleteModalOpen(false)
        }
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 400 },
            bgcolor: "var(--ax-surface)",
            border: "1px solid var(--ax-border)",
            borderRadius: "var(--ax-radius-lg)",
            boxShadow: "var(--ax-shadow-lg)",
            px: 4,
            py: 3.5,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              bgcolor: "var(--ax-red-soft)",
              color: "var(--ax-red)",
              display: "grid",
              placeItems: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <DeleteOutlineIcon />
          </Box>
          <Typography
            sx={{
              fontFamily: "var(--ax-font-heading)",
              fontWeight: 500,
              fontSize: "1.1rem",
              color: "var(--ax-ink)",
              letterSpacing: "-0.015em",
            }}
          >
            Delete this user?
          </Typography>
          <Typography
            sx={{
              mt: 1,
              color: "var(--ax-ink-2)",
              fontSize: "0.86rem",
              lineHeight: 1.55,
            }}
          >
            This action will permanently remove the selected employee profile.
          </Typography>
          <Stack direction="row" mt={3} gap={1.5} justifyContent="center">
            <Button
              variant="outlined"
              onClick={() => setDeleteModalOpen(false)}
              disabled={deletingUserLoading}
              sx={{ minWidth: 110 }}
            >
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleDelete}
              disabled={deletingUserLoading}
              sx={{ minWidth: 110 }}
            >
              {deletingUserLoading ? "Deleting..." : "Delete"}
            </Button>
          </Stack>
        </Box>
      </Modal>
      <ViewUser
        open={open}
        handleClose={handleClose}
        handleOpen={handleOpen}
        currentRow={currentRow}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiPaper-root": {
            mt: 0.5,
            minWidth: 180,
            borderRadius: "12px",
            border: "1px solid var(--ax-border)",
            boxShadow: "var(--ax-shadow)",
          },
        }}
      >
        <Stack sx={{ p: 0.5 }}>
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{
              cursor: "pointer",
              px: 1.25,
              py: 1,
              borderRadius: "8px",
              "&:hover": { bgcolor: "var(--ax-surface-hover)" },
            }}
            onClick={() => {
              setOpenDrawer(true);
              handlePopoverClose();
            }}
          >
            <AddIcon sx={{ fontSize: 16, color: "var(--ax-ink-2)" }} />
            <Typography sx={{ fontSize: "0.84rem", color: "var(--ax-ink)" }}>
              Add Access
            </Typography>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{
              cursor: "pointer",
              px: 1.25,
              py: 1,
              borderRadius: "8px",
              "&:hover": { bgcolor: "var(--ax-surface-hover)" },
            }}
            onClick={() => {
              handleModalOpen(currentRow);
              handlePopoverClose();
            }}
          >
            <EditIcon sx={{ fontSize: 16, color: "var(--ax-ink-2)" }} />
            <Typography sx={{ fontSize: "0.84rem", color: "var(--ax-ink)" }}>
              Edit User
            </Typography>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{
              cursor: "pointer",
              px: 1.25,
              py: 1,
              borderRadius: "8px",
              "&:hover": { bgcolor: "var(--ax-red-soft)" },
            }}
            onClick={() => {
              setDeleteModalOpen(true);
              setDeleteUser(currentRow);
              handlePopoverClose();
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 16, color: "var(--ax-red)" }} />
            <Typography sx={{ fontSize: "0.84rem", color: "var(--ax-red)" }}>
              Delete User
            </Typography>
          </Stack>
        </Stack>
      </Popover>
      <ToastContainer />
    </>
  );
}

export default ListOfUsers;
